import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { jbColors } from 'theme/jbColors';
import { palette } from 'theme/palette';
import { theme } from 'theme';
import { LoadingButton } from 'components/common/LoadingButton';
import { setShownPage } from 'reducers/shownPageReducer';
import { FormikValues, useFormikContext } from 'formik';
import { sendVideoIdentificationFormData } from 'services/video-identification/sendVideoIdentificationFormData';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { fetchProductsServicesThunk } from 'reducers/tableReducer';
import { useNavigate } from 'react-router';
import { closeModal, openModal } from 'reducers/modalReducer';
import { ProductServiceOutput, ViDetails, ViResponse } from 'pco-product-service-internal-consumer';
import { unwrapResult } from '@reduxjs/toolkit';
import { AppDispatch } from 'store';
import moment from 'moment';

// TODO - see if we can make styled component of this (same styles)
export const videoIdentificationStyles = {
  actions: {
    width: '50%',
    marginLeft: '-4em',
    maxHeight: '10%',
    paddingTop: theme.spacing(0),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(6.5),
    position: 'fixed',
    bottom: 0,
    zIndex: 2,
    boxShadow: '2px -1px 1px rgba(0,0,0, 0.1)',
    marginTop: theme.spacing(2),
    backgroundColor: jbColors.core.stone[20],
    height: '160px',
    display: 'flex',
    justifyContent: 'space between',
    alignItems: 'center',
  },
  tooltip: {
    background: 'black',
  },
};

export const VideoIdentificationFormActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const partnerId = useSelector(selectPartnerId);
  const { values, errors, isSubmitting, setSubmitting } = useFormikContext<FormikValues>();
  const isInviteDisabled = Object.keys(errors).length !== 0;

  const missingFields: React.ReactNode = isInviteDisabled ? (
    <>
      <p>The following fields are missing: </p>
      {Object.keys(errors).map((key, i) => {
        // @ts-expect-error dynamic key access
        return <p key={i}>- {errors[`${key}`]}</p>;
      })}
    </>
  ) : (
    ''
  );

  const handleCancel = () => {
    dispatch(setShownPage(''));
  };

  const handleSubmitVIForm = async () => {
    // todo modify once backend is ready
    if (partnerId) {
      const dtoToSend: ViDetails = {
        partnerUid: partnerId,
        businessRelationshipNumber: values.businessRelationshipNumbers,
        emailAddress: values.eMailAddresses,
        mobileNumber: values.mobileNumbers,
        name: {
          gender: values.name.gender,
          lastName: values.name.lastName,
          middleName: values.name.middleName,
          firstName: values.name.firstName,
        },
        address: {
          street: values.address.street,
          country: values.address.country,
          city: values.address.city,
          zip: values.address.zip,
        },
        // The generated client serializes dateOfBirth via `.toISOString().substring(0, 10)` (UTC),
        // so pin UTC midnight to the local calendar day to keep the day from shifting.
        dateOfBirth: values.dateOfBirth
          ? new Date(`${moment(values.dateOfBirth).format('YYYY-MM-DD')}T00:00:00Z`)
          : undefined,
        nationality: values.nationalities,
        clmUserInfo: {
          uNumber: values.clmUserInfo.uNumber,
          fullName: values.clmUserInfo.fullName,
          emailAddress: values.clmUserInfo.emailAddress,
        },
        language: values.language,
      };

      try {
        const data: ViResponse = await sendVideoIdentificationFormData(dtoToSend);
        setSubmitting(false);
        const resultAction = await dispatch(fetchProductsServicesThunk({ partnerId, navigate }));
        const originalPromiseResult = unwrapResult(resultAction);
        const updatedTableItems: ProductServiceOutput[] = originalPromiseResult?.productsServicesOutput ?? [];

        if (updatedTableItems.find((item) => item.id === data.serviceAgreementId)) {
          navigate(`/partner/${partnerId}/videoIdentification/${data.serviceAgreementId}`);
        } else {
          navigate(`/partner?partnerNumber=${partnerId}`);
        }
      } catch (err: unknown) {
        console.error(err);
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: err,
              okHandler: () => {
                setSubmitting(false);
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when sending Video Identification form data',
                userErrorMessage: `An error occurred when sending Video Identification form data. Please try again later.`,
              },
            },
          }),
        );
      }
    }
  };

  return (
    <Box data-testid="video-identification-form-actions" sx={videoIdentificationStyles.actions}>
      <Box
        style={{
          width: '95%',
          display: 'flex',
          justifyContent: 'space-between',
          maxWidth: '95%',
        }}
      >
        <Button
          id="cancel-video-identification-button"
          data-testid="cancel-video-identification-button"
          onClick={handleCancel}
          variant="outlined"
          style={{ color: palette.black }}
        >
          Cancel
        </Button>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Tooltip title={missingFields} sx={videoIdentificationStyles.tooltip} placement="bottom-end">
            <span>
              <LoadingButton
                style={{ marginRight: 0 }}
                id="invite-video-identification-button"
                data-testid="invite-video-identification-button"
                variant="contained"
                onClick={handleSubmitVIForm}
                color="primary"
                loading={isSubmitting}
                disabled={isInviteDisabled}
                type={'submit'}
              >
                Invite
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
